import { ProductEntry } from "@/types/product/productStep.types";

/**
 * Supported functional units for product measurements
 * Based on diagram specifications for unit conversions
 */
export enum FunctionalUnit {
  CUBIC_METERS = "m3",
  SQUARE_METERS = "m2",
  KILOGRAMS = "kg",
  TONNES = "t",
}

/**
 * Default thickness for m2 to m3 conversion (2cm)
 * Used when thickness is not specified in the functional unit
 * Based on diagram specifications
 * NOTE: This is in CENTIMETERS, will be converted to meters in calculations
 */
const DEFAULT_THICKNESS_CM = 2;

/**
 * Default density for volume to mass conversion (1 t/m³)
 * Used when density is not specified in the product entry
 * Based on diagram specifications
 */
const DEFAULT_DENSITY = 1;

/**
 * Rounding precision for tonne conversions
 * Based on diagram specifications
 */
const TONNE_PRECISION = 6;

/**
 * Result of convertToTonnes, including value and default usage flags
 */
export interface ConvertToTonnesResult {
  value: number;
  usedDefaultThickness: boolean;
  usedDefaultDensity: boolean;
}

/**
 * Convert a product entry's production to tonnes based on its functional unit
 * Returns value and flags for default usage.
 *
 * @param entry The product entry to convert
 * @returns {ConvertToTonnesResult} The production in tonnes, rounded, and flags for default usage
 */
export const convertToTonnes = (entry: ProductEntry): ConvertToTonnesResult => {
  let usedDefaultThickness = false;
  let usedDefaultDensity = false;
  let result = 0;

  if (!entry || entry.annualProduction < 0) {
    console.warn("Invalid input: entry is null or annualProduction is negative");
    return { value: 0, usedDefaultThickness: false, usedDefaultDensity: false };
  }

  if (!Object.values(FunctionalUnit).includes(entry.functionalUnit.unit as FunctionalUnit)) {
    console.warn(`Invalid functional unit: ${entry.functionalUnit.unit}`);
    return { value: 0, usedDefaultThickness: false, usedDefaultDensity: false };
  }

  switch (entry.functionalUnit.unit) {
    case FunctionalUnit.CUBIC_METERS: {
      const density = entry.density || DEFAULT_DENSITY;
      if (!entry.density) usedDefaultDensity = true;
      const totalVolumeM3 = entry.annualProduction * entry.functionalUnit.quantity;
      // Density is already in t/m³, so result is directly in tonnes
      result = totalVolumeM3 * density;
      break;
    }
    case FunctionalUnit.SQUARE_METERS: {
      // Convert thickness from cm to meters for volume calculation
      const thicknessCm = entry.functionalUnit.thickness || DEFAULT_THICKNESS_CM;
      const thicknessM = thicknessCm / 100;
      if (!entry.functionalUnit.thickness) usedDefaultThickness = true;
      const areaDensity = entry.density || DEFAULT_DENSITY;
      if (!entry.density) usedDefaultDensity = true;
      const area = entry.annualProduction * entry.functionalUnit.quantity;
      const volumeM3 = area * thicknessM;
      // Density is already in t/m³, so result is directly in tonnes
      result = volumeM3 * areaDensity;
      break;
    }
    case FunctionalUnit.KILOGRAMS: {
      result = (entry.annualProduction * entry.functionalUnit.quantity) / 1000;
      break;
    }
    case FunctionalUnit.TONNES: {
      result = entry.annualProduction * entry.functionalUnit.quantity;
      break;
    }
    default:
      console.warn(`Unhandled functional unit: ${entry.functionalUnit.unit}`);
      return { value: 0, usedDefaultThickness: false, usedDefaultDensity: false };
  }

  return {
    value: Math.round(result * Math.pow(10, TONNE_PRECISION)) / Math.pow(10, TONNE_PRECISION),
    usedDefaultThickness,
    usedDefaultDensity,
  };
};

/**
 * Get the mass of one functional unit in tonnes.
 * Used when applying per-tonne factors to a single functional unit.
 *
 * @param entry The product entry
 * @returns Mass of one functional unit in tonnes
 */
export const getFunctionalUnitMassInTonnes = (entry: ProductEntry): number => {
  const { functionalUnit, density } = entry;
  const d = density || DEFAULT_DENSITY;

  switch (functionalUnit.unit) {
    case FunctionalUnit.TONNES:
      return functionalUnit.quantity;
    case FunctionalUnit.KILOGRAMS:
      return functionalUnit.quantity / 1000;
    case FunctionalUnit.CUBIC_METERS:
      return functionalUnit.quantity * d;
    case FunctionalUnit.SQUARE_METERS: {
      const thicknessCm = functionalUnit.thickness || entry.espessura || 0;
      const thicknessM = thicknessCm / 100;
      return functionalUnit.quantity * thicknessM * d;
    }
    default:
      return 0;
  }
};
